import { useTranslation } from "react-i18next";
import { CostCenter, Integration, OrderLine } from "/app/src/models";
import { useOrderFields } from "/app/src/components/apps/orderBuilder/settings/fields/orderFieldsData";
import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { costCenterService, orderLineService } from "/app/src/services";
import { Form, Input, Select, SubmitButton } from "formik-antd";
import { SearchFilter } from "/app/src/components/generic/components/searchFilter";
import GridWrapper from "/app/src/components/generic/gridWrapper";
import { Formik, FormikHelpers, FormikProps } from "formik";
import { getProcessingStatusResponse } from "/app/src/types/processing";
import { handlePromiseError } from "/app/src/helpers/api";

interface FormValues {
  costCenterId?: number;
  Info1?: string;
  Info2?: string;
  Info3?: string;
  Info4?: string;
  Info5?: string;
}

/**
 * Renders the current order line form for updating order line details.
 * @param {getProcessingStatusResponse|undefined} props.processingStatus
 * @param {Object} props - Component props.
 * @param {Integration} props.integration - Integration object containing id. - Processing status response or undefined.
 * @returns {JSX.Element} The Formik form component for the current order line.
 */
export default function CurrentOrderLine({
  integration,
  processingStatus,
}: {
  integration: Integration;
  processingStatus: getProcessingStatusResponse | undefined;
}) {
  const { t } = useTranslation();
  const { orderLineFields } = useOrderFields(integration.id, "line");
  const queryClient = useQueryClient();

  const { data: selectedOrderLine } = useQuery({
    queryKey: ["orderLine", processingStatus?.masterOrderLineId],
    queryFn: () => {
      return orderLineService.getSingle(processingStatus?.masterOrderLineId);
    },
    enabled: Boolean(processingStatus?.masterOrderLineId),
    initialData: { order_line: {} },
    select: (data: { order_line: OrderLine }) => {
      return data.order_line;
    },
  });

  const { mutateAsync: updateOrderLine } = useMutation({
    mutationFn: (values: OrderLine) => {
      return orderLineService
        .updateSingle(values.id, values)
        .then(handlePromiseError);
    },
    onSuccess: (response) => {
      queryClient.setQueryData(
        ["orderLine", processingStatus?.masterOrderLineId],
        response,
      );
    },
  });

  const updateOrderLineHandler = useCallback(
    async (values: FormValues, actions: FormikHelpers<FormValues>) => {
      if (selectedOrderLine) {
        const updatedLine: OrderLine = {
          id: selectedOrderLine.id,
          ...values,
        };
        await updateOrderLine(updatedLine);
        actions.resetForm();
      }
    },
    [selectedOrderLine, updateOrderLine],
  );

  const calculateInitialValues = useCallback(() => {
    // Set initial values based on selectedOrderLine data, the initial values should match the orderLineFields that are set
    const initialValues: FormValues = {};
    orderLineFields.forEach((field) => {
      if (selectedOrderLine && selectedOrderLine[field.name] !== undefined) {
        initialValues[field.name] = selectedOrderLine[field.name];
      } else {
        initialValues[field.name] = "";
      }
    });
    return initialValues;
  }, [orderLineFields, selectedOrderLine]);

  const { data: costCenters } = useQuery({
    queryKey: ["costCenters"],
    queryFn: () => {
      return costCenterService.getAll();
    },
    initialData: { cost_centers: [] },
    select: (data: { cost_centers: CostCenter[] }) => data.cost_centers,
  });

  const mapCostCenters = useCallback((costCenter: CostCenter) => {
    return (
      <Select.Option key={costCenter.id} value={costCenter.id}>
        {costCenter.name}
      </Select.Option>
    );
  }, []);

  const createDynamicOrderLineFieldsForm = useCallback(() => {
    return (
      <>
        {orderLineFields.map((field) => (
          <Form.Item
            key={field.name}
            hasFeedback={false}
            name={field.name}
            label={field.values}
          >
            {field.name === "costCenterId" ? (
              <SearchFilter
                name={field.name}
                mapOptionsFn={mapCostCenters}
                list={costCenters}
              />
            ) : (
              <Input name={field.name} size="large" />
            )}
          </Form.Item>
        ))}
      </>
    );
  }, [costCenters, mapCostCenters, orderLineFields]);

  const lineForm: (props: FormikProps<FormValues>) => JSX.Element = useCallback(
    ({ dirty, isSubmitting }) => {
      return (
        <Form layout="vertical">
          <div className="flex items-center justify-between">
            <h2 className="text-[18px] mb-0">
              {t("translation:line_details")} - #{selectedOrderLine?.number}
            </h2>
            <SubmitButton
              disabled={!dirty || isSubmitting}
              size="large"
              color="primary"
            >
              Update Line
            </SubmitButton>
          </div>
          <GridWrapper responsive={{ sm: 1, md: 1, lg: 3 }}>
            {createDynamicOrderLineFieldsForm()}
          </GridWrapper>
        </Form>
      );
    },
    [createDynamicOrderLineFieldsForm, selectedOrderLine?.number, t],
  );

  return (
    <Formik
      enableReinitialize // Reinitialize form when initialValues changes
      initialValues={calculateInitialValues()}
      component={lineForm}
      onSubmit={updateOrderLineHandler}
    />
  );
}
